import {
  buildSvgExportFrame,
  buildSvgMarkup,
  downloadNamedDataUrl,
  downloadNamedSvg,
  renderExportDataUrl,
  resolveExportBackgroundColor,
  type ExportBoundsInput,
  type SvgExportConfig,
} from './export';

export abstract class ExportManagerBase<Options extends object> {
  protected abstract readonly prefix: string;

  protected abstract getDataUrl(options?: Options): string;

  protected abstract isInverted(): boolean;

  protected abstract getSvgExportConfig(): SvgExportConfig;

  protected abstract getSvgBounds(): ExportBoundsInput;

  protected abstract buildSvgContent(
    translate: { x: number; y: number },
    options: { transparent: boolean }
  ): string;

  protected renderRasterDataUrl(options: {
    bounds: ExportBoundsInput;
    padding: number;
    scale?: number;
    transparent?: boolean;
    emptySize?: number;
    lightColor?: string;
    darkColor?: string;
    render: Parameters<typeof renderExportDataUrl>[0]['render'];
  }): string {
    const backgroundOptions: Parameters<typeof renderExportDataUrl>[0]['background'] = {
      transparent: options.transparent ?? false,
      inverted: this.isInverted(),
    };
    if (options.lightColor !== undefined) {
      backgroundOptions.lightColor = options.lightColor;
    }
    if (options.darkColor !== undefined) {
      backgroundOptions.darkColor = options.darkColor;
    }

    const exportOptions: Parameters<typeof renderExportDataUrl>[0] = {
      bounds: options.bounds,
      padding: options.padding,
      background: backgroundOptions,
      render: options.render,
    };
    if (options.scale !== undefined) {
      exportOptions.scale = options.scale;
    }
    if (options.emptySize !== undefined) {
      exportOptions.emptySize = options.emptySize;
    }
    return renderExportDataUrl(exportOptions);
  }

  exportPNG(options: Options = {} as Options): void {
    const dataUrl = this.getDataUrl(options);
    downloadNamedDataUrl(dataUrl, this.prefix, 'png');
  }

  getCanvasDataUrl(options: Options = {} as Options): string {
    return this.getDataUrl(options);
  }

  exportSVG(options: { transparent?: boolean } = {}): void {
    const transparent = options.transparent ?? false;
    const config = this.getSvgExportConfig();
    const bounds = this.getSvgBounds();

    const frameOptions: Parameters<typeof buildSvgExportFrame>[0] = {
      bounds,
      padding: config.padding,
      fallbackSize: config.fallbackSize ?? { width: 100, height: 100 },
    };
    if (config.viewBoxOnEmpty !== undefined) {
      frameOptions.viewBoxOnEmpty = config.viewBoxOnEmpty;
    }
    const svgFrame = buildSvgExportFrame(frameOptions);

    const { width, height, translate } = svgFrame.frame;
    const content = this.buildSvgContent(translate, { transparent });

    const backgroundOptions: Parameters<typeof resolveExportBackgroundColor>[0] = {
      transparent,
      inverted: this.isInverted(),
    };
    if (config.darkColor !== undefined) {
      backgroundOptions.darkColor = config.darkColor;
    }
    const backgroundColor = resolveExportBackgroundColor(backgroundOptions);

    const markupOptions: Parameters<typeof buildSvgMarkup>[0] = {
      width,
      height,
      viewBox: svgFrame.viewBox,
      backgroundColor,
      content,
    };
    if (config.backgroundMode !== undefined) {
      markupOptions.backgroundMode = config.backgroundMode;
    }
    const svg = buildSvgMarkup(markupOptions);

    downloadNamedSvg(svg, this.prefix);
  }
}
